"use client";

import type { Dispatch, JSX, SetStateAction } from "react";
import { useEffect, useState } from "react";
import {
  Button,
  ButtonIcon,
  FileUploadField,
  Image,
  Input,
  Modal,
  QuestionPreview,
  Text,
  View
} from "@/components";
import { useLayout } from "@/providers";
import type { Question, QuestionType } from "@/types/elearning";
import { resolveFileSrc } from "@/utils/elearning/fileUrl";
import { i18nEnumKey } from "@/utils/elearning/i18nEnumKey";

/** Payload de alta/edición que la página envía al backend. */
export interface QuestionPayload {
  type: QuestionType;
  statement: string;
  /** URL interna del archivo subido; `null` limpia la imagen guardada (PATCH). */
  imageUrl?: string | null;
  explanation?: string;
  topic?: string;
  options: Array<{ text: string; isCorrect: boolean }>;
}

export interface QuestionEditorProps {
  open: boolean;
  toggleOpen: Dispatch<SetStateAction<boolean>>;
  /** Pregunta a editar; `null` para un alta. */
  question: Question | null;
  /** Guardar: la página resuelve el POST/PATCH contra el banco. */
  onSave: (payload: QuestionPayload) => void;
  saving: boolean;
}

/** Tipos con exactamente UNA opción correcta (regla del backend). */
const SINGLE_ANSWER_TYPES: QuestionType[] = ["singleChoice", "trueFalse"];

const TIPOS: QuestionType[] = ["singleChoice", "multipleChoice", "trueFalse"];

interface OptionDraft {
  text: string;
  isCorrect: boolean;
}

const emptyOptions = (): OptionDraft[] => [
  { text: "", isCorrect: true },
  { text: "", isCorrect: false }
];

/** Solo URLs absolutas o rutas: `next/image` tira error con cualquier otro `src`. */
const isPreviewableUrl = (url: string): boolean =>
  /^(https?:\/\/|\/)/.test(url);

/**
 * @name QuestionEditor
 * @description Alta/edición de una pregunta del banco (G6): tipo, enunciado,
 * imagen con vista previa (señales/situaciones), explicación (feedback del quiz),
 * tema y opciones con su marca de correcta. Respeta las reglas del
 * dominio: mínimo dos opciones, al menos una correcta y exactamente una para los
 * tipos de respuesta única (el backend las revalida); verdadero/falso usa sus dos
 * opciones fijas. Mientras el formulario es inválido, indica la regla pendiente.
 *
 * @param {QuestionEditorProps} props - Pregunta (o alta), guardado y estado.
 * @returns {JSX.Element} El editor en un modal.
 */
const QuestionEditor = ({
  open,
  toggleOpen,
  question,
  onSave,
  saving
}: QuestionEditorProps): JSX.Element => {
  const { t } = useLayout();

  const [type, setTipo] = useState<QuestionType>("singleChoice");
  const [statement, setEnunciado] = useState("");
  const [imagenUrl, setImagenUrl] = useState("");
  const [imagenError, setImagenError] = useState(false);
  const [explanation, setExplicacion] = useState("");
  const [topic, setTema] = useState("");
  const [options, setOpciones] = useState<OptionDraft[]>(emptyOptions);
  /** Alterna formulario ↔ vista previa; el borrador vive en el estado, no se pierde. */
  const [mostrarPreview, setMostrarPreview] = useState(false);

  /** Sincroniza el formulario al abrir (alta limpia o edición precargada). */
  useEffect(() => {
    if (!open) return;
    setImagenError(false);
    setMostrarPreview(false);
    if (question) {
      setTipo(question.type);
      setEnunciado(question.statement);
      setImagenUrl(question.imageUrl ?? "");
      setExplicacion(question.explanation ?? "");
      setTema(question.topic ?? "");
      setOpciones(
        question.options.map((o) => ({
          text: o.text,
          isCorrect: o.isCorrect
        }))
      );
    } else {
      setTipo("singleChoice");
      setEnunciado("");
      setImagenUrl("");
      setExplicacion("");
      setTema("");
      setOpciones(emptyOptions());
    }
  }, [open, question]);

  const singleAnswer = SINGLE_ANSWER_TYPES.includes(type);
  const esVerdaderoFalso = type === "trueFalse";

  /**
   * Cambio de tipo: verdadero/falso precarga sus dos opciones fijas; al salir de
   * verdadero/falso se arranca de cero (sus textos no aplican a otro tipo); y al
   * pasar a un tipo de respuesta única se conserva solo la primera correcta, para
   * que el estado no quede desincronizado de los radios.
   */
  const handleTipoChange = (nuevo: QuestionType) => {
    if (nuevo === type) return;
    const anterior = type;
    setTipo(nuevo);
    if (nuevo === "trueFalse") {
      setOpciones([
        { text: t("true"), isCorrect: true },
        { text: t("false"), isCorrect: false }
      ]);
      return;
    }
    setOpciones((prev) => {
      const base = anterior === "trueFalse" ? emptyOptions() : prev;
      if (!SINGLE_ANSWER_TYPES.includes(nuevo)) return base;
      const primera = base.findIndex((o) => o.isCorrect);
      return base.map((o, i) => ({ ...o, isCorrect: i === primera }));
    });
  };

  const handleCorrectaChange = (index: number, checked: boolean) => {
    setOpciones((prev) =>
      prev.map((opcion, i) => {
        // Respuesta única: marcar una desmarca las demás.
        if (singleAnswer) return { ...opcion, isCorrect: i === index };
        return i === index ? { ...opcion, isCorrect: checked } : opcion;
      })
    );
  };

  const correct = options.filter((o) => o.isCorrect).length;

  /** Primera regla pendiente (guía al autor mientras Guardar está deshabilitado). */
  let reglaPendiente: string | null = null;
  if (!statement.trim()) reglaPendiente = t("questionStatementRequiredNote");
  else if (!options.every((o) => o.text.trim()))
    reglaPendiente = t("optionTextsRequiredNote");
  else if (singleAnswer && correct !== 1)
    reglaPendiente = t("singleCorrectAnswerNote");
  else if (!singleAnswer && correct < 1)
    reglaPendiente = t("multipleCorrectAnswersNote");

  const valido = options.length >= 2 && !reglaPendiente;

  const imagenTrimmed = imagenUrl.trim();
  // El proxy /api/files resuelve las URLs internas (auth por cookie same-origin).
  const previewSrc =
    !imagenError && isPreviewableUrl(imagenTrimmed)
      ? resolveFileSrc(imagenTrimmed)
      : null;

  const handleSave = () => {
    if (!valido) return;
    onSave({
      type,
      statement: statement.trim(),
      // null explícito: en el PATCH limpia una imagen previamente guardada.
      imageUrl: imagenTrimmed || null,
      ...(explanation.trim() ? { explanation: explanation.trim() } : {}),
      ...(topic.trim() ? { topic: topic.trim() } : {}),
      options: options.map((o) => ({
        text: o.text.trim(),
        isCorrect: o.isCorrect
      }))
    });
  };

  const inputClass =
    "w-full px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-background text-sm text-foreground";
  const chipClass = (activo: boolean) =>
    `rounded-full text-xs font-medium transition-colors ${
      activo
        ? "bg-primary text-white"
        : "bg-slate-100 dark:bg-slate-800 text-muted-foreground hover:text-foreground"
    }`;

  return (
    <Modal
      open={open}
      toggleOpen={toggleOpen}
      width="max-w-2xl bg-card text-card-foreground rounded-2xl shadow-xl"
    >
      <View className="flex flex-col gap-4 p-4 max-h-[80vh] overflow-y-auto">
        <View className="flex flex-wrap items-center justify-between gap-3">
          <Text type="h2" className="text-lg font-semibold text-foreground">
            {question ? t("editQuestion") : t("newQuestion")}
          </Text>
          {/* Editar ↔ vista previa: el borrador no se pierde al alternar. */}
          <View className="flex gap-2">
            <Button
              action={() => setMostrarPreview(false)}
              label={t("edit")}
              aria-pressed={!mostrarPreview}
              className={`px-3 py-1.5 ${chipClass(!mostrarPreview)}`}
            >
              {t("edit")}
            </Button>
            <Button
              action={() => setMostrarPreview(true)}
              label={t("preview")}
              aria-pressed={mostrarPreview}
              className={`px-3 py-1.5 ${chipClass(mostrarPreview)}`}
            >
              {t("preview")}
            </Button>
          </View>
        </View>

        {mostrarPreview ? (
          <QuestionPreview
            question={{
              type,
              statement,
              imageUrl: imagenTrimmed || null,
              explanation,
              options
            }}
          />
        ) : (
          <>
            {/* Tipo */}
            <View className="flex flex-wrap gap-2">
              {TIPOS.map((opcionTipo) => (
                <Button
                  key={opcionTipo}
                  action={() => handleTipoChange(opcionTipo)}
                  label={t(i18nEnumKey("questionType", opcionTipo))}
                  className={`px-3 py-1.5 ${chipClass(type === opcionTipo)}`}
                >
                  {t(i18nEnumKey("questionType", opcionTipo))}
                </Button>
              ))}
            </View>

            {/* Enunciado */}
            <View className="flex flex-col gap-1.5">
              <Text
                type="label"
                htmlFor="q-statement"
                className="text-sm font-medium text-foreground"
              >
                {t("questionStatement")}
              </Text>
              <textarea
                id="q-statement"
                name="q-statement"
                value={statement}
                onChange={(e) => setEnunciado(e.target.value)}
                placeholder={t("questionStatementPlaceholder")}
                rows={2}
                className={`${inputClass} resize-y`}
              />
            </View>

            {/* Imagen (señales / situaciones): subida al módulo files, con vista previa */}
            <View className="flex flex-col gap-1.5">
              <Text
                type="label"
                htmlFor="q-imagen-upload"
                className="text-sm font-medium text-foreground"
              >
                {`${t("questionImage")} (${t("optional")})`}
              </Text>
              <FileUploadField
                id="q-imagen-upload"
                name="q-imagen-upload"
                accept="image/png,image/jpeg,image/webp"
                onUploaded={(url) => {
                  setImagenUrl(url);
                  setImagenError(false);
                }}
                value={imagenTrimmed}
              />
              {!imagenTrimmed && (
                <Text className="text-xs text-muted-foreground">
                  {t("imageRecommendedNote")}
                </Text>
              )}
              {imagenError && imagenTrimmed && (
                <Text className="text-xs text-destructive">
                  {t("imageLoadError")}
                </Text>
              )}
              {previewSrc && (
                <View className="flex justify-center rounded-xl border border-slate-100 dark:border-slate-800 p-2">
                  <Image
                    src={previewSrc}
                    alt=""
                    width={320}
                    height={240}
                    unoptimized
                    // max-h-44 y no max-h-40: el átomo Image mapea los sufijos de
                    // clases h-*/w-* contra ImageSizes, y "40" colisiona con el
                    // reverse mapping del enum ("4x" = 40), pisando height con el
                    // string "4x" (next/image tira error). 44 no está en el enum.
                    className="rounded-lg object-contain max-h-44 w-auto!"
                    onError={() => setImagenError(true)}
                  />
                </View>
              )}
              {imagenTrimmed && (
                <Button
                  action={() => {
                    setImagenUrl("");
                    setImagenError(false);
                  }}
                  label={t("removeImage")}
                  className="w-fit px-4 py-1.5 rounded-full border border-slate-200 dark:border-slate-700 text-xs font-medium text-destructive hover:border-destructive/60"
                >
                  {t("removeImage")}
                </Button>
              )}
            </View>

            {/* Opciones */}
            <View className="flex flex-col gap-2">
              <Text className="text-sm font-medium text-foreground">
                {t("answerOptions")}
              </Text>
              <Text className="text-xs text-muted-foreground">
                {esVerdaderoFalso
                  ? t("fixedTrueFalseOptionsNote")
                  : singleAnswer
                    ? t("singleCorrectAnswerNote")
                    : t("multipleCorrectAnswersNote")}
              </Text>
              {options.map((opcion, index) => (
                <View
                  // biome-ignore lint/suspicious/noArrayIndexKey: <las opciones en borrador no tienen id estable>
                  key={index}
                  className="flex items-center gap-3"
                >
                  <Input
                    id={`q-isCorrect-${index}`}
                    name={singleAnswer ? "q-isCorrect" : `q-isCorrect-${index}`}
                    type={singleAnswer ? "radio" : "checkbox"}
                    checked={opcion.isCorrect}
                    onChange={(e) =>
                      handleCorrectaChange(index, e.target.checked)
                    }
                    className="accent-primary w-4 h-4 shrink-0"
                    aria-label={`${t("correctOption")} ${index + 1}`}
                  />
                  <Input
                    id={`q-opcion-${index}`}
                    name={`q-opcion-${index}`}
                    value={opcion.text}
                    readOnly={esVerdaderoFalso}
                    onChange={(e) =>
                      setOpciones((prev) =>
                        prev.map((o, i) =>
                          i === index ? { ...o, text: e.target.value } : o
                        )
                      )
                    }
                    placeholder={`${t("option")} ${index + 1}`}
                    className={`flex-1 ${inputClass} ${
                      esVerdaderoFalso ? "opacity-70" : ""
                    }`}
                  />
                  {(!esVerdaderoFalso || options.length > 2) && (
                    <ButtonIcon
                      action={() =>
                        setOpciones((prev) =>
                          prev.filter((_, i) => i !== index)
                        )
                      }
                      iconLeft={{
                        name: "fa-solid fa-trash-can",
                        className: "text-sm!"
                      }}
                      disabled={options.length <= 2}
                      className="p-2 text-destructive/70 hover:text-destructive disabled:opacity-30"
                      aria-label={`${t("removeOption")} ${index + 1}`}
                    />
                  )}
                </View>
              ))}
              {!esVerdaderoFalso && (
                <Button
                  action={() =>
                    setOpciones((prev) => [
                      ...prev,
                      { text: "", isCorrect: false }
                    ])
                  }
                  label={t("addOption")}
                  className="w-fit px-4 py-1.5 rounded-full border border-slate-200 dark:border-slate-700 text-xs font-medium text-foreground hover:border-primary/50"
                >
                  {`+ ${t("addOption")}`}
                </Button>
              )}
            </View>

            {/* Explicación y tema */}
            <View className="flex flex-col gap-1.5">
              <Text
                type="label"
                htmlFor="q-explanation"
                className="text-sm font-medium text-foreground"
              >
                {`${t("explanation")} (${t("optional")})`}
              </Text>
              <textarea
                id="q-explanation"
                name="q-explanation"
                value={explanation}
                onChange={(e) => setExplicacion(e.target.value)}
                placeholder={t("explanationPlaceholder")}
                rows={2}
                className={`${inputClass} resize-y`}
              />
            </View>
            <View className="flex flex-col gap-1.5">
              <Text
                type="label"
                htmlFor="q-topic"
                className="text-sm font-medium text-foreground"
              >
                {`${t("topic")} (${t("optional")})`}
              </Text>
              <Input
                id="q-topic"
                name="q-topic"
                value={topic}
                onChange={(e) => setTema(e.target.value)}
                placeholder={t("topicPlaceholder")}
                className={inputClass}
              />
            </View>
          </>
        )}

        <View className="flex items-center justify-end gap-3 pt-2">
          {reglaPendiente && (
            <Text className="mr-auto text-xs text-muted-foreground">
              {reglaPendiente}
            </Text>
          )}
          <Button
            action={() => toggleOpen(false)}
            label={t("cancel")}
            className="px-5 py-2.5 rounded-full border border-slate-200 dark:border-slate-700 text-sm font-medium"
          >
            {t("cancel")}
          </Button>
          <Button
            action={handleSave}
            loading={saving}
            disabled={saving || !valido}
            label={t("save")}
            className="px-5 py-2.5 rounded-full bg-primary text-white text-sm font-medium disabled:opacity-50"
          >
            {t("save")}
          </Button>
        </View>
      </View>
    </Modal>
  );
};

export default QuestionEditor;
